import React, { useState, useEffect } from 'react';
import { getApiClient } from '../../api/apiClient';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const defaultTemplates = [
  {
    id: 'unpaid-inquiry',
    name: 'Payment Inquiry',
    subject: 'VIN Check Payment Required',
    content: 'Hello,\n\nWe noticed that your VIN check for {vin} is still pending payment. Could you please let us know if you encountered any issues during checkout?\n\nWe\'re here to help resolve any payment concerns.\n\nBest regards,\nServicegest Team'
  },
  {
    id: 'payment-reminder',
    name: 'Payment Reminder',
    subject: 'Friendly Payment Reminder - VIN Check',
    content: 'Hello,\n\nThis is a friendly reminder about your unpaid VIN check session for {vin}.\n\nTo complete your payment and access your report, please visit: [Payment Link]\n\nIf you have any questions, feel free to contact us.\n\nBest regards,\nServicegest Team'
  },
  {
    id: 'follow-up',
    name: 'Follow-up',
    subject: 'VIN Check - Need Assistance?',
    content: 'Hello,\n\nWe wanted to follow up on your VIN check request for {vin}. We\'re here to assist you with completing your report.\n\nIs there anything we can help you with?\n\nBest regards,\nServicegest Team'
  },
  {
    id: 'custom',
    name: 'Custom Message',
    subject: '',
    content: ''
  }
];

const Outreach = () => {
  const { t } = useLanguage();
  const [unpaidVinChecks, setUnpaidVinChecks] = useState([]);
  const [filteredChecks, setFilteredChecks] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [template, setTemplate] = useState(defaultTemplates[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    const fetchUnpaidVinChecks = async () => {
      setLoading(true);
      try {
        const client = getApiClient();
        const response = await client.get('/admin/vinChecks');
        const unpaidChecks = (response.data || []).filter(check => !check.paid);
        setUnpaidVinChecks(unpaidChecks);
        setFilteredChecks(unpaidChecks);
      } catch (err) {
        setError('Failed to fetch unpaid VIN checks.');
      } finally {
        setLoading(false);
      }
    };
    fetchUnpaidVinChecks();
  }, []);

  useEffect(() => {
    const filtered = unpaidVinChecks.filter(check => 
      check.vin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      check.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      check.phone?.includes(searchTerm)
    );
    setFilteredChecks(filtered);
  }, [searchTerm, unpaidVinChecks]);

  const handleTemplateChange = (e) => {
    const selected = defaultTemplates.find(t => t.id === e.target.value);
    setTemplate(selected);
    setSubject(selected.subject);
    setMessage(selected.content.replace('{vin}', selectedSession?.vin || '[VIN]'));
  };

  const handleSessionSelect = (check) => {
    setSelectedSession(check);
    setSubject(template.subject);
    setMessage(template.content.replace('{vin}', check.vin || '[VIN]'));
    setSuccess('');
    setError('');
  };

  const handleSend = async () => {
    if (!selectedSession || !message.trim()) return;
    setSending(true);
    setSuccess('');
    setError('');
    try {
      // Replace with your actual email sending logic/API
      await new Promise(res => setTimeout(res, 1500)); // Simulate network
      setSuccess(`Message sent successfully to ${selectedSession.email}!`);
      setShowConfirm(false);
      // Optionally mark as contacted
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const getTimeSince = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  return (
    <div className="py-6 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      <div className="mx-auto px-4 sm:px-6 md:px-8">
        
        {/* Header Section */}
        <div className="mb-8">
          
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="bg-white overflow-hidden shadow-lg rounded-xl border border-slate-200">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                      <ExclamationTriangleIcon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">{t('totalUnpaid')}</dt>
                      <dd>
                        <div className="text-2xl font-bold text-slate-900">{unpaidVinChecks.length}</div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 px-6 py-4 border-t border-slate-100">
                <div className="text-sm text-slate-600 font-semibold">
                  {t('sessionsRequiringAttention')}
                </div>
              </div>
            </div>
            
            <div className="bg-white overflow-hidden shadow-lg rounded-xl border border-slate-200">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                      <MagnifyingGlassIcon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">{t('filteredResults')}</dt>
                      <dd>
                        <div className="text-2xl font-bold text-slate-900">{filteredChecks.length}</div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 px-6 py-4 border-t border-slate-100">
                <div className="text-sm text-slate-600 font-semibold">
                  {t('matchingSearchCriteria')}
                </div>
              </div>
            </div>
            
            <div className="bg-white overflow-hidden shadow-lg rounded-xl border border-slate-200">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                      <CheckCircleIcon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">{t('selected')}</dt>
                      <dd>
                        <div className="text-2xl font-bold text-slate-900">{selectedSession ? 1 : 0}</div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 px-6 py-4 border-t border-slate-100">
                <div className="text-sm text-slate-600 font-semibold">
                  {t('readyForOutreach')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="bg-white shadow-lg rounded-xl border border-slate-200 flex items-center justify-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="relative w-16 h-16 mx-auto mb-6">
                <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-blue-600 rounded-full animate-spin border-t-transparent"></div>
              </div>
              <p className="text-xl font-medium text-slate-900 mb-2">{t('loadingOutreachData')}</p>
              <p className="text-slate-600">{t('loadingUnpaidSessions')}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
            
            {/* Sessions List */}
            <div className="xl:col-span-2">
              <div className="bg-white shadow-lg rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-white to-slate-50">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <h2 className="text-xl font-bold text-slate-900">
                      {t('unpaidSessions')}
                      <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {filteredChecks.length} {t('sessions')}
                      </span>
                    </h2>
                  </div>
                  
                  {/* Enhanced Search */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={t('searchByVinEmailPhone')}
                      className="w-full pl-12 pr-12 py-3 text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all duration-200"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
                    </div>
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="max-h-[600px] overflow-y-auto">
                  {filteredChecks.length === 0 ? (
                    <div className="p-12 text-center">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <MagnifyingGlassIcon className="w-8 h-8 text-slate-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-3">
                        {searchTerm ? t('noMatchingSessions') : t('noUnpaidSessions')}
                      </h3>
                      <p className="text-slate-600 max-w-md mx-auto">
                        {searchTerm 
                          ? t('adjustSearchTerms') 
                          : t('allSessionsPaid')
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {filteredChecks.map((check, idx) => (
                        <div
                          key={idx}
                          className={`p-6 cursor-pointer transition-all duration-200 hover:bg-slate-50 ${
                            selectedSession?.sessionId === check.sessionId 
                              ? 'bg-blue-50 border-r-4 border-blue-500' 
                              : ''
                          }`}
                          onClick={() => handleSessionSelect(check)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-4">
                                <span className="font-mono text-sm font-bold text-slate-900 bg-slate-100 px-3 py-1.5 rounded-lg border">
                                  {check.vin}
                                </span>
                                <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5 animate-pulse"></div>
                                  {t('unpaid')}
                                </span>
                              </div>
                              
                              <div className="space-y-2">
                                <div className="flex items-center">
                                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                    <EnvelopeIcon className="w-4 h-4 text-blue-600" />
                                  </div>
                                  <span className="text-slate-700 font-medium truncate">{check.email}</span>
                                </div>
                                
                                {check.phone && (
                                  <div className="flex items-center">
                                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                                      <PhoneIcon className="w-4 h-4 text-green-600" />
                                    </div>
                                    <span className="text-slate-700 font-medium">{check.phone}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="text-right">
                              <div className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg">
                                <ClockIcon className="w-3 h-3 mr-1" />
                                {check.createdAt ? getTimeSince(check.createdAt) : t('unknown')}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Message Composer */}
            <div className="xl:col-span-2">
              <div className="bg-white shadow-lg rounded-xl border border-slate-200 sticky top-6 overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-purple-50">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                      <PaperAirplaneIcon className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-lg font-bold text-slate-900">{t('composeMessage')}</h2>
                  </div>
                </div>

                {selectedSession ? (
                  <div className="p-6 space-y-6">
                    {/* Enhanced Recipient Info */}
                    <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-5 border border-slate-200">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <UserIcon className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-blue-600 mb-1">{t('sendingTo')}</p>
                          <p className="font-bold text-slate-900 truncate">{selectedSession.email}</p>
                          <p className="text-sm text-slate-600 mt-2">
                            <span className="font-medium">VIN:</span> 
                            <span className="ml-2 font-mono bg-white px-2 py-1 rounded border text-xs">
                              {selectedSession.vin}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Template Selection */}
                    <div>
                      <label className="block text-sm font-bold text-slate-900 mb-3">
                        {t('messageTemplate')}
                      </label>
                      <select 
                        className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-base"
                        value={template.id} 
                        onChange={handleTemplateChange}
                      >
                        {defaultTemplates.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-sm font-bold text-slate-900 mb-3">
                        {t('emailSubject')}
                      </label>
                      <input
                        type="text"
                        className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-base"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder={t('enterEmailSubject')}
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-bold text-slate-900 mb-3">
                        {t('messageContent')}
                      </label>
                      <textarea
                        className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white text-base"
                        rows={8}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t('typeMessageHere')}
                      />
                      <div className="flex justify-between items-center mt-2">
                        <div className="text-sm text-slate-500">
                          {message.length} {t('characters')}
                        </div>
                        <div className="text-sm text-slate-500">
                          {message.split('\n').length} {t('lines')}
                        </div>
                      </div>
                    </div>

                    {/* Enhanced Send Button */}
                    <button
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-purple-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold text-base shadow-lg hover:shadow-xl"
                      onClick={() => setShowConfirm(true)}
                      disabled={sending || !message.trim() || !subject.trim()}
                    >
                      {sending ? (
                        <span className="flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                          {t('sendingMessage')}
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                          {t('sendMessage')}
                        </span>
                      )}
                    </button>

                    {/* Enhanced Status Messages */}
                    {success && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <div className="flex items-start">
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                            <CheckCircleIcon className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-green-800">{t('messageSent')}</p>
                            <p className="text-sm text-green-700 mt-1">{success}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                        <div className="flex items-start">
                          <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                            <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-red-800">{t('error')}</p>
                            <p className="text-sm text-red-700 mt-1">{error}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <ChatBubbleLeftRightIcon className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">{t('selectSession')}</h3>
                    <p className="text-slate-600">{t('chooseCustomerSession')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Confirmation Modal */}
        {showConfirm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-lg shadow-2xl rounded-xl bg-white">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <PaperAirplaneIcon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{t('confirmSendMessage')}</h3>
                <p className="text-slate-600">
                  {t('confirmSendMessageTo')}{' '}
                  <span className="font-semibold text-slate-900">{selectedSession?.email}</span>?
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <button
                  className="py-3 px-6 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-semibold"
                  onClick={() => setShowConfirm(false)}
                >
                  {t('cancel')}
                </button>
                <button
                  className="py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold shadow-lg"
                  onClick={handleSend}
                >
                  {t('sendMessage')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Outreach;
